/**
 * How a countdown arrives on screen. Every open gets a quick staggered
 * entrance in the look's own manner, and the numbers count up to the real
 * values. The very first open after onboarding (markFirstRun) gets a slower,
 * bigger version, and each view adds its own flourish on top.
 */
const FIRST_RUN_KEY = "cuntdown:first-run";

export function markFirstRun(): void {
  try {
    sessionStorage.setItem(FIRST_RUN_KEY, "1");
  } catch {
    // no sessionStorage: the countdown just gets the everyday entrance
  }
}

/** True once, on the first countdown render after markFirstRun(). */
export function takeFirstRun(): boolean {
  try {
    const first = sessionStorage.getItem(FIRST_RUN_KEY) === "1";
    sessionStorage.removeItem(FIRST_RUN_KEY);
    return first;
  } catch {
    return false;
  }
}

export const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * - stick: Sticker. Parts slap down from slightly above with a twist, like
 *   stickers being pressed on.
 * - float: Neumorphic. Parts float up out of a blur into focus.
 * Animates translate/rotate/scale rather than transform so it
 * composes with the parts' own tilts.
 */
export type EntranceStyle = "stick" | "float";

export function playEntrance(parts: (HTMLElement | null)[], style: EntranceStyle, first: boolean): void {
  if (reducedMotion()) return;
  const els = parts.filter((p): p is HTMLElement => !!p && p.offsetParent !== null);
  const stagger = first ? 95 : 38;
  const duration = first ? 620 : 380;
  const lead = first ? 120 : 0;
  els.forEach((el, i) => {
    const delay = lead + i * stagger;
    const opts: KeyframeAnimationOptions = { duration, delay, fill: "backwards" };
    if (style === "stick") {
      const tilt = (i % 2 ? 1 : -1) * (first ? 5 : 2.5);
      el.animate(
        [
          { opacity: 0, translate: `0 ${first ? -26 : -12}px`, rotate: `${tilt}deg`, scale: first ? "1.08" : "1.03" },
          { opacity: 1, translate: "0 2px", rotate: `${-tilt * 0.25}deg`, scale: ".99", offset: 0.62 },
          { opacity: 1, translate: "0 0", rotate: "0deg", scale: "1" },
        ],
        { ...opts, easing: "cubic-bezier(.3,.7,.4,1)" }
      );
    } else {
      el.animate(
        [
          { opacity: 0, translate: `0 ${first ? 26 : 12}px`, scale: first ? ".94" : ".98", filter: `blur(${first ? 10 : 5}px)` },
          { opacity: 1, translate: "0 0", scale: "1", filter: "blur(0)" },
        ],
        { ...opts, duration: duration + 80, easing: "cubic-bezier(.2,.8,.2,1)" }
      );
    }
  });
}

/**
 * Counts `el` up from zero to its value. While it runs, setCount() only
 * records the latest value (the clock keeps ticking underneath) and the
 * count lands on it. `done` runs at the end, for anything sized to the text.
 */
export function countUp(el: HTMLElement, to: number, format: (n: number) => string, ms: number, done?: () => void): void {
  if (reducedMotion() || to <= 0) {
    done?.();
    return;
  }
  el.dataset.counting = format(to);
  const t0 = performance.now();
  const step = (now: number) => {
    if (!el.isConnected) return;
    const p = Math.min(1, (now - t0) / ms);
    const eased = 1 - Math.pow(1 - p, 4);
    if (p < 1) {
      el.textContent = format(Math.round(to * eased));
      requestAnimationFrame(step);
    } else {
      el.textContent = el.dataset.counting ?? format(to);
      delete el.dataset.counting;
      done?.();
    }
  };
  requestAnimationFrame(step);
}

/** Writes a number's text unless a count-up is running on it, in which case the count lands on it. */
export function setCount(el: HTMLElement, text: string): void {
  if (el.dataset.counting !== undefined) el.dataset.counting = text;
  else el.textContent = text;
}
